import axios from 'axios';
import React from 'react';
import 'styles/main.css';

import { Router, Route, Redirect, browserHistory } from 'react-router';
import { Provider } from 'react-redux';
import App from 'pages/App';
import BusinessInfo from 'pages/Register/BusinessInfo';
import Kyb from 'pages/Kyb';
import Dashboard from 'pages/Dashboard';
import NotFound from 'pages/NotFound';
import Version from 'pages/Version';
import CapitalRoutes from 'routes/Capital';
import Fresh from 'pages/Fresh';
import Outlet, { List as OutletList } from 'pages/Outlets';
import {
  isSubscriptionExpired,
  showSubscriptionExpiredModal,
  hideSubscriptionExpiredModal,
} from 'actions/subscriptionExpired';
import {
  setAuthFromLocal,
  setProfile,
  setAuthState,
  setPermissionsState,
  changeBusinessTypesData,
  changeBusinessCategoriesAllData,
  showActiveApplicationsAllData,
} from 'actions';
import {
  reset,
  error as actNotifError,
  success as actNotifSuccess,
} from 'actions/notifications';
import { syncHistoryWithStore } from 'react-router-redux';
import BillingRedirect from 'pages/BillingRedirect';
import FeatureFlags from 'pages/FeatureFlags';
import moment from 'moment';
import * as apiProfile from 'api/profile';
import _ from 'lodash';
import { isExternalRedirect } from 'utils/sso';
import isMokaposUser from 'selectors/isMokaposUser';
import isSubscriptionExpiredSelector from 'selectors/isSubscriptionExpired';
import { subscribePushNotification } from 'reducers/pushNotification/actions';
import FeatureFlagProvider from 'components/FeatureFlagProvider';
import { hasFeature } from 'utils/featureFlags';
import { navigateToUrl } from 'single-spa';
import AppRoutes from 'routes/Apps';
import BusinessLibraryRoutes from 'routes/BusinessLibrary';
import CustomerRoutes from 'routes/Customers';
import EmployeeRoutes from 'routes/Employees';
import IngredientRoutes from 'routes/Ingredient';
import InventoryRoutes from 'routes/Inventory';
import LibraryRoutes from 'routes/Library';
import OnlineStoreRoutes from 'routes/OnlineStore/OnlineStoreRoutes';
import OutletLibraryRoutes from 'routes/OutletLibrary';
import PaymentRoutes from 'routes/Payments';
import ReportRoutes from 'routes/Reports';
import SettingRoutes from 'routes/Settings';
import TableManagementRoutes from 'routes/TableManagement';
import * as clevertapOpenPlatformMyApp from 'clevertap/openPlatform/myApp';
import axiosInstance from 'utils/axiosInstance';
import { getAccessToken, invalidateSession } from 'utils/auth';
import {
  ALLOWED_BUSINESS_CATEGORY_ID,
  ALLOWED_EMAIL,
} from 'utils/onlineStoreUtils';
import { I18nLoader } from '@moka-bo/i18n';
import {
  getBusinessTypes,
  getBusinessCategoriesAll,
  getActiveBusinessApplication,
  loggedIn as isCurrentlyLoggedIn,
} from './api/auth';

export default reduxStore => {
  const routes = () => {
    const history = syncHistoryWithStore(browserHistory, reduxStore);
    return (
      <Provider store={reduxStore}>
        <FeatureFlagProvider>
          <I18nLoader language="id">
            <Router history={history}>
              <Route path="/version" component={Version} />
              <Route
                onEnter={onEnterProtectedRoute}
                component={App}
                onChange={onProtectedRouteChange}
              >
                <Redirect from="/" to="/dashboard" />
                <Route
                  path="/dashboard"
                  component={Dashboard}
                  onEnter={handleAccess}
                />
                <Route path="/flags" component={FeatureFlags} />
                <ReportRoutes
                  path="/reports"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <BusinessLibraryRoutes
                  path="/business-library"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <OutletLibraryRoutes
                  path="/outlet-library"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <LibraryRoutes
                  path="/library"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <SettingRoutes
                  path="/settings"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <CustomerRoutes
                  path="/customers"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <EmployeeRoutes
                  path="/employees"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <TableManagementRoutes
                  path="/table-management"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <InventoryRoutes
                  path="/inventory"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <IngredientRoutes
                  path="/ingredient"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <Route path="/outlets" component={Outlet}>
                  <Route
                    path="/outlets/list"
                    component={OutletList}
                    onEnter={handleAccess}
                  />
                </Route>
                <AppRoutes
                  path="/apps"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <Route
                  path="/billings/redirect"
                  component={BillingRedirect}
                  onEnter={handleAccess}
                />
                <CapitalRoutes
                  path="/capital"
                  onEnter={handleAccess}
                  onChange={handleAccessOnChange}
                />
                <Route path="/fresh" component={Fresh} onEnter={handleAccess} />
                <PaymentRoutes
                  path="/payments"
                  onEnter={canVisitPaymentPages}
                />
                <OnlineStoreRoutes
                  path="/online-store"
                  onEnter={canVisitOnlineStorePages}
                  canVisitGoogleOnlineStore={canVisitGoogleOnlineStore}
                  canVisitFacebookOnlineStore={canVisitFacebookOnlineStore}
                />
                <Route path="/business-info" component={BusinessInfo} />
                <Route path="/kyb" component={Kyb} />
              </Route>
              <Route path="*" component={NotFound} />
            </Router>
          </I18nLoader>
        </FeatureFlagProvider>
      </Provider>
    );
  };

  function canVisitPaymentPages(nextState, replace, callback) {
    const canVisit = hasFeature('enable-payment-configuration');
    if (!canVisit) {
      replace('/404');
    }
    callback();
  }

  function canVisitOnlineStorePages(routes, replace) {
    let replaced = false;
    const wrappedReplace = route => {
      replaced = true;
      replace(route);
    };

    handleAccess(routes, wrappedReplace);

    if (replaced) {
      return;
    }

    const canVisit =
      (isOnlineStoreEmailAllowed() && isOnlineStoreBusinessCategoryAllowed()) ||
      (hasFeature('enable-shopping-ext') &&
        isOnlineStoreBusinessCategoryAllowed());

    if (!canVisit) {
      replace('/');
    }
  }

  function canVisitGoogleOnlineStore(routes, replace) {
    const canVisit =
      (isOnlineStoreEmailAllowed() && isOnlineStoreBusinessCategoryAllowed()) ||
      (hasFeature('enable-google-shopping-ext') &&
        isOnlineStoreBusinessCategoryAllowed());

    if (!canVisit) {
      replace('/');
    }
  }

  function canVisitFacebookOnlineStore(routes, replace) {
    const canVisit =
      (isOnlineStoreEmailAllowed() && isOnlineStoreBusinessCategoryAllowed()) ||
      (hasFeature('enable-facebook-shopping-ext') &&
        isOnlineStoreBusinessCategoryAllowed());

    if (!canVisit) {
      replace('/');
    }
  }

  function isOnlineStoreEmailAllowed() {
    const state = reduxStore.getState();
    const { email } = _.get(state, 'moka.user');

    return ALLOWED_EMAIL.some(e => e === email);
  }

  function isOnlineStoreBusinessCategoryAllowed() {
    const state = reduxStore.getState();
    const {
      business: { business_category_id: businessCategoryId },
    } = _.get(state, 'moka.user');

    return ALLOWED_BUSINESS_CATEGORY_ID.some(id => id === businessCategoryId);
  }

  async function onEnterProtectedRoute(nextState, replace, callback) {
    const loggedIn = isCurrentlyLoggedIn();
    const intendedPath = nextState.location.pathname;

    // Check auth status, redirect to login if failure
    // TODO we will keep this until `/version` and 404 are moved out
    if (!loggedIn) {
      navigateToUrl(`/login?next=${intendedPath}`);
      return;
    }

    // This point onwards, a valid session is assumed

    /* DANGER: This piece of code is a bit messy.
     *
     * From what I understand, it tries to fetch user profile, etc for
     * `showActiveApplicationsAllData`.
     *
     * Then for some reason, `setAuthFromLocal` is called.
     *
     * TODO:
     * - Since profile is fetched here, there is no need to fetch profile again
     *   in the `pageRequest` saga of most pages
     * - We might not need to fetch permission again
     * - `setAuthFromLocal` in the middle is a smell, investigate
     */
    const [
      {
        data: {
          is_owner: isOwner,
          id: userId,
          first_name: firstName,
          permissions,
        },
      },
      { data: businessTypes },
      { data: businessCategoriesAll },
    ] = await Promise.all([
      apiProfile.getProfile(),
      getBusinessTypes(),
      getBusinessCategoriesAll(),
    ]);
    reduxStore.dispatch(changeBusinessTypesData(businessTypes));
    reduxStore.dispatch(changeBusinessCategoriesAllData(businessCategoriesAll));

    if (isOwner) {
      // only owner can fetch active application

      /*  using `then` for handle set active application and push to clevertap
       * `getActiveBusinessApplication` will be run async and do actions on `then` so no need await for get activeApplications
       * this actions will be optional
       * error fetch `getActiveBusinessApplication` will be ignore
       */
      getActiveBusinessApplication(userId).then(res => {
        const activeApplications = _.get(res.data, ['data', 'apps'], []).map(
          app => app.name
        );
        reduxStore.dispatch(showActiveApplicationsAllData(activeApplications));
        clevertapOpenPlatformMyApp.myActiveAppList(activeApplications);
      });
    }

    // TODO This piece of code might not be needed anymore
    reduxStore.dispatch(setAuthFromLocal());

    reduxStore.dispatch(setPermissionsState(permissions));

    // Additional check for user subscription
    const isAccessible = ensureActiveSubscription(intendedPath, replace);

    if (isAccessible) {
      // Request Web Push permission after login
      reduxStore.dispatch(subscribePushNotification());
    }

    reduxStore.dispatch(actNotifSuccess(`Welcome back, ${firstName}`));

    // All done
    callback();
  }

  function onProtectedRouteChange(prevState, nextState, replace) {
    // reset the notification.
    reduxStore.dispatch(reset());

    ensureActiveSubscription(nextState.location.pathname, replace);
  }

  // TODO These `handleAccess*` functions need to be merged to top protection level
  function handleAccessOnChange(prevState, nextState, replace) {
    handleAccess(nextState, replace);
  }

  function handleAccess(routes, replace) {
    const state = reduxStore.getState();
    const { moka } = state;
    const { loggedIn, user } = moka;
    const hasLimitedAccess = !isMokaposUser(state);

    const limitedUrlsNotHandledByApi = [
      '/reports/sales/gross_profit',
      '/reports/sales/modifier_sales',
      '/reports/sales/category_sales',
      '/reports/sales/taxes',
      '/reports/sales/gratuity',
      '/reports/sales/staff',
      '/reports/sales/server',
    ];
    let availableRoutes = getAvailableRoutes();
    if (hasLimitedAccess) {
      availableRoutes = _.omit(availableRoutes, limitedUrlsNotHandledByApi);
    }
    const isDestinationPermitted = Boolean(
      availableRoutes[routes.location.pathname]
    );
    const isExpired = isSubscriptionExpiredSelector(state);
    if (isExpired) {
      ensureActiveSubscription(routes.location.pathname, replace);
      return;
    }

    const { next } = routes.location.query;
    if (loggedIn) {
      if (
        user.business.business_category_id === null ||
        user.business.province === null
      ) {
        browserHistory.push('/business-info');
      } else if (isExternalRedirect(next)) {
        window.location.href = next;
      } else if (next === 'gostore-merchant') {
        window.location.href = `${
          process.env.GOSTORE_AUTH_URL
        }?token=${getAccessToken()}`;
      } else if (!isDestinationPermitted) {
        if (hasLimitedAccess) {
          replace('/reports/sales/summary');
        } else if (!moka.user.is_owner) {
          replace('/settings/accounts');
        }
      } else {
        // We are good, do nothing
      }
    } else if (next) {
      replace(`/login?next=${next}`);
    } else {
      replace(`/login`);
    }
  }

  function getAvailableRoutes() {
    const state = reduxStore.getState();
    const { permissions } = state.moka;
    const limitFeaturesForPay = !isMokaposUser(state);
    const subMenuNotAccessByMokapay = ['Employee Access'];
    if (permissions) {
      const permissionsArray = permissions.reduce((memo, permission) => {
        if (subMenuNotAccessByMokapay.includes(permission.sub_menu)) {
          permission.is_allowed_for_moka_pay = false;
        }
        if (!limitFeaturesForPay || permission.is_allowed_for_moka_pay) {
          memo.push(...permission.urls);
        }
        return memo;
      }, []);

      return _.groupBy(permissionsArray);
    }
    return {};
  }

  /**
   * Verify if current user has active subscription. Redirect to billing page
   * if not.
   *
   * A boolean value is returned to indicate if the `intendedPath` is accessible.
   *
   * @param {string} intendedPath Path user wanted to visit.
   * @param {function} replace `replace` function from Router.
   */
  function ensureActiveSubscription(intendedPath, replace) {
    const state = reduxStore.getState();
    const isPathAccessible = intendedPath === '/settings/billing';
    const isExpired = isSubscriptionExpiredSelector(state);

    if (isExpired && !isPathAccessible) {
      replace('/settings/billing');
      return false;
    }

    return true;
  }

  /*
   * This request interceptor add `OUTLET_ID` header when necessary.
   *
   * For various reasons, many APIs require either `OUTLET_ID` or `OUTLET_IDS`
   * headers.
   *
   * TODO: All these except the one for auth should be set by the requesting
   * site instead.
   */
  function attachOutletIdHeaderInterceptor(config) {
    const { moka, form } = reduxStore.getState();
    const { user } = moka;
    if (config.url.includes('mokapos.com')) {
      // Existing API handling outlet ids
      if (
        user &&
        (config.url.includes('inventory/v1') ||
          config.url.includes('inventory/v2')) &&
        !config.url.includes('suppliers') &&
        !config.url.includes('/control-panel') &&
        !config.url.includes('/feature-flag/eligible-features')
      ) {
        const { outletId, outletIds } = form.search
          ? form.search.values || form.search.initial
          : {};
        config.headers.OUTLET_ID = outletId || outletIds;
        config.headers.OUTLET_IDS = user.outlets
          .map(outlet => outlet.id)
          .join();
        if (
          (config.url.includes('/items') ||
            config.url.includes('/ingredients')) &&
          config.params
        ) {
          if (config.params.outlet_id) {
            config.headers.OUTLET_ID = config.params.outlet_id;
          }
        }
      } else if (
        hasFeature('enable-outlet-id-header-for-auth') &&
        user &&
        user.outlet_id
      ) {
        // All other APIs. This outlet_id is a workaround for auth cache
        // at API Gateway.
        // Note that if there is already `OUTLET_ID` or `OUTLET_IDS`, we
        // should do nothing
        const alreadyHasHeader = Object.keys(config.headers).some(header => {
          const candidate = header.toLowerCase();
          return candidate === 'outlet_id' || candidate === 'outlet_ids';
        });
        if (!alreadyHasHeader) {
          config.headers.outlet_id = user.outlet_id.join();
        }
      }
    }
    return config;
  }

  /*
   * This response interceptor checks each `/profile` call for current user status.
   *
   * `/profile` can only be successful if user is authenticated. This interceptor
   * does several things:
   * - Keep user profile data in Redux store
   * - Check if user subscription is expired, redirect to billing page if so
   *
   * Regarding `showSubscriptionExpiredModal` and `hideSubscriptionExpiredModal`
   * actions, they control `state.subscriptionExpired.showSubscriptionExpiredModal`.
   * However this piece of data is not being used anywhere.
   */
  function checkUserProfile(response) {
    if (response.config.url.match(/profile/g)) {
      reduxStore.dispatch(setAuthState(true, response.data));
      reduxStore.dispatch(setProfile(response.data));
      if (moment(response.data.business.expired_at) <= moment()) {
        reduxStore.dispatch(isSubscriptionExpired(true));
        reduxStore.dispatch(showSubscriptionExpiredModal());
        if (window.location.pathname !== '/settings/billing') {
          browserHistory.push('/settings/billing');
        }
      } else {
        reduxStore.dispatch(isSubscriptionExpired(false));
        reduxStore.dispatch(hideSubscriptionExpiredModal());
      }
    }
    return response;
  }

  /*
   * This response interceptor handles error 401.
   * When 401 is encountered, it will invalidate all session information and
   * redirect user to login page.
   *
   * TODO When response data contains an error string, it does not. It is unknown
   * why this is so. We can possibly remove that part.
   */
  function handleError401(error) {
    if (error.response && error.response.status === 401) {
      if (
        error.response.data.error &&
        typeof error.response.data.error === 'string'
      ) {
        // trace down and find that actNotifError only accept string, please double confirm to whomever need this code
        reduxStore.dispatch(actNotifError(error.response.data.error));
        return Promise.reject(error);
      }

      // Invalidate session
      invalidateSession();
      navigateToUrl('/login');

      return Promise.reject(new Error('You must be Signed in'));
    }
    return Promise.reject(error);
  }

  // Hook up interceptors
  // FIXME: These interceptors use Redux store so we are unable to move them out
  // of here easily
  axios.interceptors.request.use(attachOutletIdHeaderInterceptor);
  axiosInstance.interceptors.request.use(attachOutletIdHeaderInterceptor);

  axios.interceptors.response.use(checkUserProfile, handleError401);
  axiosInstance.interceptors.response.use(checkUserProfile, handleError401);

  return routes;
};
